import { useState, useRef, useEffect } from 'react'
import { ChevronsUpDown, Check, Building2 } from 'lucide-react'
import { useAccount } from '../hooks/useAccount'

/* ==================================================================
 * AccountSwitcher — global top-left WORKSPACE switcher
 * ==================================================================
 * The account-level sibling of BrandSwitcher, rendered next to it in
 * AuthenticatedApp. Shown ONLY at the agency lobby (`!inBrand`) — inside
 * a brand it's the wrong altitude and reads as a page filter.
 *
 * Lists two kinds of workspace:
 *   - every ACCOUNT the user belongs to (invited to two agencies, say)
 *   - every standalone BRAND they can reach that belongs to none of
 *     those accounts — a solo brand that added them to its own Team
 *     page, which account resolution can't see at all
 *
 * Both were unreachable before it existed: pickAccount had to guess
 * which account was meant, and a standalone brand was never a candidate.
 *
 * Switching persists the choice, drops the previous account's brand
 * selection, and hard-reloads to the lobby (see useAccount.switchAccount).
 * ================================================================== */

function Avatar({ name, logo, size, ring }: { name: string; logo: string | null; size: number; ring?: boolean }) {
  return (
    <span
      style={{
        position: 'relative', width: size, height: size, flexShrink: 0, borderRadius: 10,
        boxShadow: ring ? '0 0 0 1.5px rgba(251,58,174,0.55)' : undefined,
      }}
    >
      <span style={{
        position: 'absolute', inset: 0, borderRadius: 10,
        background: 'linear-gradient(135deg, #FB3AAE 0%, #7C3AED 100%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: '#fff', fontSize: Math.round(size * 0.44), fontWeight: 700,
      }}>{(name || '?').charAt(0).toUpperCase()}</span>
      {logo && (
        <img
          src={logo}
          alt=""
          style={{ position: 'absolute', inset: 0, width: size, height: size, borderRadius: 10, objectFit: 'cover', background: 'var(--bg-tertiary)' }}
          onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none' }}
        />
      )}
    </span>
  )
}

export default function AccountSwitcher() {
  const {
    account, accounts, switchAccount,
    standaloneBrands, switchBrandWorkspace, activeWorkspace,
    isAgency, inBrand,
  } = useAccount()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  // Nothing to switch between — hidden for the single-workspace majority.
  if (!account || accounts.length + standaloneBrands.length <= 1) return null
  // Inside an AGENCY's brand this is the wrong altitude: the page is scoped to
  // one brand of one agency, and offering to jump agencies from there reads as a
  // filter on the current page. Agency home is one click away in the sidebar and
  // is where switching belongs.
  //
  // A BRAND workspace is different — it has no lobby to retreat to, so hiding it
  // there would strand the user in that brand with no way back.
  if (isAgency && inBrand) return null

  const inBrandWorkspace = activeWorkspace?.kind === 'brand'
  const activeBrandWs = inBrandWorkspace
    ? standaloneBrands.find(b => b.id === activeWorkspace.id)
    : undefined
  const activeName = inBrandWorkspace
    ? (activeBrandWs?.brand_name ?? account.name ?? 'Brand')
    : (account.name || 'Account')
  const activeLogo = inBrandWorkspace ? (activeBrandWs?.logo_url ?? null) : (account.logo_url ?? null)
  const activeEyebrow = inBrandWorkspace ? 'Brand' : account.type === 'agency' ? 'Agency' : 'Brand account'
  const label = (a: { type: string }) => (a.type === 'agency' ? 'Agency' : 'Brand account')

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{ ...trigger, ...(open ? triggerActive : {}) }}
        onMouseEnter={(e) => { if (!open) e.currentTarget.style.borderColor = 'rgba(255,255,255,0.22)' }}
        onMouseLeave={(e) => { if (!open) e.currentTarget.style.borderColor = 'var(--border-primary)' }}
        title="Switch workspace"
        aria-label={`Current workspace: ${activeName}. Switch workspace.`}
      >
        <Avatar name={activeName} logo={activeLogo} size={28} ring />
        <span style={triggerText}>
          <span style={eyebrow}>{activeEyebrow}</span>
          <span style={triggerName}>{activeName}</span>
        </span>
        <ChevronsUpDown size={15} color="var(--text-tertiary)" style={{ flexShrink: 0 }} />
      </button>

      {open && (
        <div className="brand-menu-scroll" style={menu}>
          {accounts.length > 0 && <div style={menuLabel}>Your agencies</div>}
          {accounts.map(a => {
            const isActive = !inBrandWorkspace && a.id === account.id
            const name = a.name || 'Account'
            return (
              <button
                key={a.id}
                style={{ ...item, ...(isActive ? itemActive : {}) }}
                onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = 'var(--bg-secondary)' }}
                onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = 'transparent' }}
                onClick={() => { if (!isActive) switchAccount(a.id); else setOpen(false) }}
                title={name}
              >
                <Avatar name={name} logo={a.logo_url ?? null} size={26} ring={isActive} />
                <span style={itemText}>
                  <span style={itemName}>{name}</span>
                  <span style={itemRole}>{label(a)}</span>
                </span>
                {isActive && <Check size={15} strokeWidth={2.25} color="#FB3AAE" style={{ flexShrink: 0 }} />}
              </button>
            )
          })}

          {/* Brands reached directly rather than through an agency — a solo brand
              that added this person to its own Team page. They belong to no
              account the user is a member of, so they'd be invisible otherwise. */}
          {standaloneBrands.length > 0 && (
            <>
              <div style={{ ...menuLabel, paddingTop: accounts.length ? 10 : 6 }}>Brands</div>
              {standaloneBrands.map(b => {
                const isActive = inBrandWorkspace && b.id === activeWorkspace.id
                return (
                  <button
                    key={b.id}
                    style={{ ...item, ...(isActive ? itemActive : {}) }}
                    onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = 'var(--bg-secondary)' }}
                    onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = 'transparent' }}
                    onClick={() => { if (!isActive) switchBrandWorkspace(b.id); else setOpen(false) }}
                    title={b.brand_name}
                  >
                    <Avatar name={b.brand_name} logo={b.logo_url ?? null} size={26} ring={isActive} />
                    <span style={itemText}>
                      <span style={itemName}>{b.brand_name}</span>
                      <span style={itemRole}>{b.category || 'Brand'}</span>
                    </span>
                    {isActive && <Check size={15} strokeWidth={2.25} color="#FB3AAE" style={{ flexShrink: 0 }} />}
                  </button>
                )
              })}
            </>
          )}

          <div style={menuFoot}>
            <Building2 size={12} style={{ flexShrink: 0, marginTop: 1 }} />
            <span>Switching reloads the dashboard and takes you into that workspace.</span>
          </div>
        </div>
      )}
    </div>
  )
}

// Styles deliberately mirror BrandSwitcher so the two read as one control pair,
// with squared avatars (an account, not a brand) as the only difference.
const trigger: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 10,
  maxWidth: 260, padding: '6px 12px 6px 8px',
  background: 'var(--bg-primary)',
  border: '1px solid var(--border-primary)',
  borderRadius: 12, cursor: 'pointer', fontFamily: 'inherit',
  transition: 'border-color 140ms ease',
}
const triggerActive: React.CSSProperties = { borderColor: 'rgba(251,58,174,0.45)' }
const triggerText: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 0, lineHeight: 1.2,
}
const eyebrow: React.CSSProperties = {
  fontSize: 10, fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase',
  color: 'var(--text-tertiary)',
}
const triggerName: React.CSSProperties = {
  fontSize: 13, fontWeight: 600, color: 'var(--text-primary)',
  maxWidth: 170, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
}
const menu: React.CSSProperties = {
  position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 60,
  minWidth: 268, padding: 6,
  background: 'var(--bg-primary)', border: '1px solid var(--border-primary)',
  borderRadius: 14, boxShadow: '0 16px 40px rgba(0,0,0,0.5)',
  // Opens DOWNWARD from a trigger near the top of the viewport, so a long list
  // (an agency with a dozen brands, or someone on several teams) ran off the
  // bottom and clipped whatever sat at the end. Cap to the space that exists and
  // scroll. ~120px covers the top bar the trigger lives in plus the 8px gap and a
  // bottom margin.
  maxHeight: 'calc(100dvh - 120px)', overflowY: 'auto', overscrollBehavior: 'contain',
}
const menuLabel: React.CSSProperties = {
  fontSize: 10, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase',
  color: 'var(--text-tertiary)', padding: '6px 10px 8px',
}
const item: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 10, width: '100%',
  padding: '8px 10px', borderRadius: 10, border: 'none', background: 'transparent',
  cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left',
  transition: 'background 120ms ease',
}
const itemActive: React.CSSProperties = { background: 'rgba(251,58,174,0.10)' }
const itemText: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1, lineHeight: 1.25,
}
const itemName: React.CSSProperties = {
  fontSize: 13, fontWeight: 600, color: 'var(--text-primary)',
  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
}
const itemRole: React.CSSProperties = {
  fontSize: 11, color: 'var(--text-tertiary)', textTransform: 'capitalize',
}
const menuFoot: React.CSSProperties = {
  display: 'flex', gap: 6, padding: '8px 10px 4px', marginTop: 4,
  borderTop: '1px solid var(--border-primary)',
  fontSize: 11, lineHeight: 1.45, color: 'var(--text-tertiary)',
}
